/** Small building blocks that match the prototype's classes (prototype.css). */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ApiError } from "../lib/api";
import { Icon, type IconName } from "./Icon";

export function PageHeader({
  title,
  sub,
  crumb,
  children,
  hideTitle,
}: {
  title: string;
  sub?: ReactNode;
  /** the heading stays for screen readers and the tab title, but is not shown */
  hideTitle?: boolean;
  crumb?: Array<[string, string | null]>;
  children?: ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} · Brecx Ads`;
  }, [title]);
  return (
    <div className="ph">
      <div>
        {crumb ? (
          <div className="crumb">
            {crumb.map(([label, to], i) => (
              <span key={label} style={{ display: "contents" }}>
                {to ? <Link to={to}>{label}</Link> : <span>{label}</span>}
                {i < crumb.length - 1 ? <span>/</span> : null}
              </span>
            ))}
          </div>
        ) : null}
        <h1 className={hideTitle ? "sr-only" : undefined}>{title}</h1>
        {sub ? <p className="sub">{sub}</p> : null}
      </div>
      {children ? <div className="actions">{children}</div> : null}
    </div>
  );
}

/** "How is this calculated" popover; closes on outside click and Escape. */
export function Formula({
  children,
  label = "How is this calculated?",
}: {
  children: ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        className="info"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        i
      </button>
      {open ? (
        <span className="pop" role="dialog" style={{ left: -10, top: 22, width: 290 }}>
          {children}
        </span>
      ) : null}
    </span>
  );
}

export function Kpi(p: {
  label: string;
  value: string;
  sub?: ReactNode;
  delta?: { text: string; tone: string } | null;
  hero?: boolean;
  formula?: ReactNode;
  spark?: number[];
  sparkColor?: string;
}) {
  return (
    <div className={`card kpi${p.hero ? " hero" : ""}`}>
      <div className="lbl">
        {p.label}
        {p.formula ? <Formula>{p.formula}</Formula> : null}
      </div>
      <div className="v num">{p.value}</div>
      <div className="foot">
        {p.delta ? <span className={`delta ${p.delta.tone}`}>{p.delta.text}</span> : <span />}
        {p.spark ? (
          <Sparkline values={p.spark} color={p.sparkColor ?? (p.hero ? "var(--profit)" : "var(--s1)")} />
        ) : null}
      </div>
      {p.sub ? <div className="ksub">{p.sub}</div> : null}
    </div>
  );
}

export function Sparkline({
  values,
  color = "var(--s1)",
  w = 84,
  h = 26,
}: {
  values: number[];
  color?: string;
  w?: number;
  h?: number;
}) {
  if (values.length < 2) return null;
  const lo = Math.min(...values);
  const rng = Math.max(...values) - lo || 1;
  const pts = values
    .map(
      (v, i) =>
        `${((i * w) / (values.length - 1)).toFixed(1)},${(h - 2 - ((v - lo) * (h - 4)) / rng).toFixed(1)}`,
    )
    .join(" ");
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline fill="none" stroke={color} strokeWidth={1.6} points={pts} />
    </svg>
  );
}

export function Chip({
  children,
  tone = "",
  dot,
  title,
}: {
  children: ReactNode;
  tone?: string;
  dot?: boolean;
  title?: string;
}) {
  return (
    <span className={`chip ${tone}`} title={title}>
      {dot ? <span className="d" /> : null}
      {children}
    </span>
  );
}

const CHANNEL_KIND: Record<string, string> = {
  SEARCH: "search",
  PERFORMANCE_MAX: "pmax",
  SHOPPING: "shopping",
};
const TYPE_ICON: Record<string, { icon: IconName; label: string }> = {
  search: { icon: "search", label: "Search" },
  shopping: { icon: "tag", label: "Shopping" },
  pmax: { icon: "spark", label: "Performance Max" },
  brand: { icon: "megaphone", label: "Brand" },
  display: { icon: "image", label: "Display" },
  video: { icon: "play", label: "Video" },
  demand_gen: { icon: "image", label: "Demand Gen" },
};

/** The campaign type as a small icon (like Google Ads), its name on hover and for screen readers. */
export function TypeIcon({ kind }: { kind: string | null }) {
  const k = CHANNEL_KIND[kind ?? ""] ?? (kind ?? "search").toLowerCase();
  const t = TYPE_ICON[k] ?? {
    icon: "layers" as IconName,
    label: k.charAt(0).toUpperCase() + k.slice(1).replaceAll("_", " "),
  };
  return (
    <span className={`type-icon id-${k}`} title={t.label} role="img" aria-label={t.label}>
      <Icon name={t.icon} size={13} />
    </span>
  );
}

/** Campaign type / match type identity chip. `kind`: broad | phrase | exact | pmax | shopping | brand | search, or a Google channel type. */
export function TypeChip({ kind, label }: { kind: string | null; label?: string }) {
  const k = CHANNEL_KIND[kind ?? ""] ?? (kind ?? "search").toLowerCase();
  const text =
    label ??
    { pmax: "PMax", search: "Search", shopping: "Shopping" }[k] ??
    k.charAt(0).toUpperCase() + k.slice(1);
  return (
    <span className={`chip plain id-${k}`}>
      <span className="d" />
      {text}
    </span>
  );
}

export function Tier({ tier }: { tier: string | null }) {
  return tier ? (
    <span className={`tier ${tier}`}>{tier}</span>
  ) : (
    <Chip tone="crit" title="Not advertisable at target: max CPA ≤ 0">
      —
    </Chip>
  );
}

const STATE_LABEL: Record<string, [string, string]> = {
  draft: ["Draft", "draft"],
  invalid: ["Blocked", "failed"],
  pending_approval: ["Needs approval", "pending"],
  scheduled: ["Scheduled", "scheduled"],
  approved: ["Approved", "pending"],
  applying: ["Applying", "pending"],
  applied: ["Applied", "applied"],
  partially_applied: ["Partly applied", "failed"],
  failed: ["Failed", "failed"],
  rejected: ["Rejected", "reverted"],
  cancelled: ["Cancelled", "reverted"],
  reverted: ["Reverted", "reverted"],
};
export function StatePill({ status }: { status: string }) {
  const [label, cls] = STATE_LABEL[status] ?? [status, "draft"];
  return <span className={`state ${cls}`}>{label}</span>;
}

const AUTONOMY: Record<string, [string, string]> = {
  off: ["Off", "a-off"],
  suggest: ["Suggest", "a-suggest"],
  auto_undo: ["Auto · undo", "a-undo"],
  auto: ["Auto", "a-auto"],
};
export function AutonomyPill({ level }: { level: string }) {
  const [label, cls] = AUTONOMY[level] ?? [level, "a-off"];
  return <span className={`auto ${cls}`}>{label}</span>;
}

export function Verdict({ verdict }: { verdict: string | undefined }) {
  if (verdict === "block") return <Chip tone="crit">Blocked</Chip>;
  if (verdict === "ask") return <Chip tone="warn">Needs approval</Chip>;
  return <Chip tone="good">OK</Chip>;
}

export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="state-box">
      {children}
      {action ? <div style={{ marginTop: 10 }}>{action}</div> : null}
    </div>
  );
}

export function Loading({ rows = 1 }: { rows?: number }) {
  return (
    <div className="stack" role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

export function ErrorBox({ error, retry }: { error: Error; retry?: () => void }) {
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <div className="state-box err" role="alert">
      <Icon name="alert" /> {forbidden ? "You don't have access to this." : error.message}
      {retry && !forbidden ? (
        <div style={{ marginTop: 10 }}>
          <button type="button" className="btn sm" onClick={retry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Pace({
  label,
  value,
  frac,
  expected,
}: {
  label: string;
  value: string;
  frac: number;
  expected?: number;
}) {
  const clamp = (x: number) => Math.max(0, Math.min(1, x)) * 100;
  return (
    <div className="pace">
      <div className="row between">
        <span>{label}</span>
        <b className="num">{value}</b>
      </div>
      <div
        className="track"
        role="meter"
        aria-valuenow={Math.round(clamp(frac))}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <i style={{ width: `${clamp(frac)}%` }} />
        {expected !== undefined ? <b style={{ left: `${clamp(expected)}%` }} /> : null}
      </div>
    </div>
  );
}

/**
 * A compact filter dropdown; the first option is "no filter". When another option is picked it
 * takes the accent outline, so it shows at a glance what the list is filtered by.
 */
export function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
}) {
  const on = value !== options[0]?.[0];
  return (
    <select
      className={`input filter-select${on ? " on" : ""}`}
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map(([v, text]) => (
        <option key={v} value={v}>
          {text}
        </option>
      ))}
    </select>
  );
}

/** "× Clear 2 filters": shown while a search or filter is on, in the accent so it is easy to find. */
export function ClearFilters({ count, onClear }: { count: number; onClear: () => void }) {
  if (count <= 0) return null;
  return (
    <button type="button" className="btn sm clear-filters" onClick={onClear}>
      <Icon name="x" size={12} />
      Clear {count === 1 ? "filter" : `${count} filters`}
    </button>
  );
}

/** Segmented control. */
export function Seg<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([v, l]) => (
        <button
          type="button"
          key={String(v)}
          className={v === value ? "on" : ""}
          aria-pressed={v === value}
          onClick={() => onChange(v)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
